import React, {useEffect, useMemo, useState} from 'react';
import {Button, FormGroup, Label} from 'reactstrap';
import ConditionalPrice from '../../components/ConditionalPrice';
import CountrySelect from '../../components/CountrySelect';
import {buildPriceTable} from '../../utils/credits';
import Countries from './countries.json';

import './VersionPricing.scss';

const DEFAULT = 'default';

const countryName = code => Countries.regions.find(r => r.code === code)?.name || code;

const hasBands = entry => Boolean(entry?.mid?.length || entry?.premium?.length);

/**
 * The version's price tables: one default, plus an optional override per
 * territory. A site is charged from its country's table, or the default when
 * the version has none for it (see utils/credits.js getSitePriceTable).
 *
 * Only one table is shown at a time, picked from the territory switcher —
 * every selected region gets a tab, and so does any territory that already
 * has prices, so nothing that affects a price is ever hidden.
 */
const VersionPricing = ({ versionData, distributor, handleChange }) => {
    const [active, setActive] = useState(DEFAULT);
    // Territories added from the country picker on a worldwide release.
    const [added, setAdded] = useState([]);
    const [pickerOpen, setPickerOpen] = useState(false);
    const tiers = versionData.tier;
    const territoryPrices = useMemo(() => versionData.territoryPrices || [], [versionData.territoryPrices]);
    const isWorldwide = versionData.countries?.includes('worldwide');

    const territories = useMemo(() => {
        const selected = (versionData.countries || []).filter(code => code !== 'worldwide');
        const priced = territoryPrices.map(t => t.countryCode);
        return [...new Set([...selected, ...priced, ...(isWorldwide ? added : [])])].sort();
    }, [versionData.countries, territoryPrices, added, isWorldwide]);

    // A territory removed from both the regions and the price list has no tab left.
    useEffect(() => {
        if (active !== DEFAULT && !territories.includes(active)) setActive(DEFAULT);
    }, [active, territories]);

    const activeEntry = territoryPrices.find(t => t.countryCode === active);
    const distributorTerritory = distributor?.countries?.find(c => c.countryCode === active);
    const activeTable = useMemo(() => (
        active === DEFAULT
            ? versionData.conditionalPrice
            : { mid: activeEntry?.mid || [], premium: activeEntry?.premium || [] }
    ), [active, activeEntry, versionData.conditionalPrice]);

    const canFetch = Boolean(versionData.releaseDate && distributor && tiers?.length);

    const setTerritoryPrices = value => handleChange({ target: { name: 'territoryPrices', value } });

    const setTerritoryTable = (code, table) => {
        const entry = { countryCode: code, mid: table.mid || [], premium: table.premium || [] };
        const exists = territoryPrices.some(t => t.countryCode === code);
        setTerritoryPrices(exists
            ? territoryPrices.map(t => (t.countryCode === code ? entry : t))
            : [...territoryPrices, entry]);
    };

    // Re-seeds the default table and every territory the distributor prices
    // (all of them for a worldwide release, else the selected regions). Prices
    // set here for territories the distributor has no pricing for are kept.
    const fetchFromDistributor = () => {
        if (!canFetch) return;
        const wanted = (distributor.countries || []).filter(c => isWorldwide || versionData.countries?.includes(c.countryCode));
        const fetched = wanted.map(c => ({
            countryCode: c.countryCode,
            ...buildPriceTable(c.defaults, versionData.releaseDate, tiers),
        }));
        const fetchedCodes = fetched.map(t => t.countryCode);
        const kept = territoryPrices.filter(t => !fetchedCodes.includes(t.countryCode));

        handleChange([
            { target: { name: 'conditionalPrice', value: buildPriceTable(distributor.distributorDefaults, versionData.releaseDate, tiers) } },
            { target: { name: 'territoryPrices', value: [...kept, ...fetched] } },
        ]);
    };

    // A new override starts from the distributor's prices for that territory
    // when there are any, otherwise from the version default.
    const addTerritoryPrices = (code = active) => {
        const fromDistributor = distributor?.countries?.find(c => c.countryCode === code);
        const seed = fromDistributor && canFetch
            ? buildPriceTable(fromDistributor.defaults, versionData.releaseDate, tiers)
            : versionData.conditionalPrice || { mid: [], premium: [] };
        setTerritoryTable(code, seed);
    };

    const removeTerritoryPrices = () => setTerritoryPrices(territoryPrices.filter(t => t.countryCode !== active));

    // An added territory whose prices were dropped: not a selected region and no prices.
    const isRemovableTab = active !== DEFAULT && !activeEntry && !versionData.countries?.includes(active);
    const removeTab = () => {
        setAdded(prev => prev.filter(code => code !== active));
        setActive(DEFAULT);
    };

    const addableTerritories = Countries.regions.filter(r => !territories.includes(r.code));

    const activeName = countryName(active);

    return (
        <FormGroup className="version-pricing">
            <div className="pricing-header">
                <Label className="mb-0">Conditional Pricing</Label>
                <Button color="link" size="sm" className="pricing-fetch" onClick={fetchFromDistributor} disabled={!canFetch}>
                    <span className="fas fa-rotate me-2" aria-hidden="true" />
                    Fetch default pricing
                </Button>
            </div>

            {!tiers?.length ? (
                <div className="pricing-notice">
                    <span className="fas fa-triangle-exclamation me-2" aria-hidden="true" />
                    Select at least one tier to set prices.
                </div>
            ) : !versionData.releaseDate ? (
                <div className="pricing-notice">
                    <span className="fas fa-triangle-exclamation me-2" aria-hidden="true" />
                    Set a release date to set prices.
                </div>
            ) : (
                <div className="pricing-panel">
                    <div className="territory-tabs" role="tablist" aria-label="Pricing territory">
                        {[DEFAULT, ...territories].map(code => {
                            const own = code !== DEFAULT && hasBands(territoryPrices.find(t => t.countryCode === code));
                            const name = code === DEFAULT ? 'Default' : countryName(code);
                            return (
                                <button
                                    key={code}
                                    type="button"
                                    role="tab"
                                    aria-selected={active === code}
                                    aria-label={own ? `${code}, own prices` : undefined}
                                    className={`territory-tab${active === code ? ' active' : ''}${code === DEFAULT || own ? ' has-prices' : ''}`}
                                    onClick={() => setActive(code)}
                                    title={code === DEFAULT ? 'Sites without territory prices' : name}
                                >
                                    {code === DEFAULT ? 'Default' : code}
                                    {own && <span className="territory-dot" aria-hidden="true" />}
                                </button>
                            );
                        })}
                        {isWorldwide && (
                            <button type="button" className="territory-tab territory-add" onClick={() => setPickerOpen(true)}>
                                <span className="fas fa-plus" aria-hidden="true" />
                                Add territory
                            </button>
                        )}
                    </div>

                    {active === DEFAULT ? (
                        <p className="pricing-context">Sites without territory prices pay these.</p>
                    ) : activeEntry ? (
                        <div className="pricing-context">
                            <span>Sites in {activeName} pay these prices.</span>
                            <Button color="secondary" outline size="sm" onClick={removeTerritoryPrices}>
                                <span className="fas fa-rotate-left me-2" aria-hidden="true" />
                                Use default pricing
                            </Button>
                        </div>
                    ) : (
                        <div className="pricing-inherit">
                            <span>Sites in {activeName} pay the default prices.</span>
                            <div className="pricing-inherit-actions">
                                {isRemovableTab && (
                                    <Button color="link" size="sm" className="text-decoration-none text-white-50" onClick={removeTab}>
                                        Remove {active}
                                    </Button>
                                )}
                                <Button color="primary" size="sm" onClick={() => addTerritoryPrices()}>
                                    <span className="fas fa-plus me-2" aria-hidden="true" />
                                    {distributorTerritory ? `Use ${distributor.name} ${active} pricing` : `Set ${active} prices`}
                                </Button>
                            </div>
                        </div>
                    )}

                    {(active === DEFAULT || activeEntry) && (
                        <ConditionalPrice
                            key={active}
                            handleChange={e => (active === DEFAULT
                                ? handleChange(e)
                                : setTerritoryTable(active, e.target.value))}
                            conditionalPrice={activeTable}
                            tiers={tiers}
                            emptyText={active === DEFAULT ? 'No prices yet. Sites cannot order this tier.' : `${activeName} sites in this tier pay the default.`}
                        />
                    )}
                </div>
            )}

            <CountrySelect
                hideInput
                multiple={false}
                value=""
                countries={addableTerritories}
                onChange={code => {
                    // Adding a territory is for giving it its own prices, so it starts with them.
                    setAdded(prev => [...prev, code]);
                    addTerritoryPrices(code);
                    setActive(code);
                }}
                modal={pickerOpen}
                toggle={() => setPickerOpen(open => !open)}
            />
        </FormGroup>
    );
};

export default VersionPricing;
